import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import SemanticHeader from '../Chapter/semantic-header';
import { useMatomoTrackEventOnChange } from '../Hooks/index';
import { isMediumViewport } from '../Hooks/useMedia';
import MediaGalleryImage from './components/image';
import MediaGalleryPagination from './components/pagination';
import MediaGalleryVideo from './components/video';
import useWindowLocationHref from '../Hooks/useWindowLocation';

const MediaGallery = (props) => {
  const href = useWindowLocationHref();
  const {
    Heading,
    Media,
    Translations,
    CurrentPageProps,
    Dir,
    ShowPagination,
  } = props;

  const containsMedia = (media) => {
    let hasMedia = false;
    try {
      media?.length > 0 ? (hasMedia = true) : (hasMedia = false);
    } catch (error) {
      throw new Error('Du måste ha minst en bild i bildspelet');
    }

    return hasMedia;
  };

  const totalItems = Media?.length;

  const hasMedia = containsMedia(Media);
  const isMobile = isMediumViewport();
  const isArticlePage = CurrentPageProps.PageTypeName === 'ArticlePage';
  const isThreeSlidesLayout = !isArticlePage && !isMobile;
  const isFullWidthLayout = totalItems === 4;
  const galleryIsScrollable = totalItems > 4 || isArticlePage;
  const alwaysShowCurrentImageIndicator =
    !ShowPagination && galleryIsScrollable;

  const getSlideWidth = () => {
    if (isArticlePage && !isMobile) {
      return '100%';
    }
    if ((galleryIsScrollable && !isMobile) || (totalItems < 4 && !isMobile)) {
      return '33%';
    }
    if (isFullWidthLayout && !isMobile) {
      return '25%';
    }
    return '83%';
  };

  const slideWidth = getSlideWidth();

  const scrollContainerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const maxVisiblePaginationDots = 10;
  const [visiblePaginationRange, setVisiblePaginationRange] = useState([0, 9]);

  const updatePaginationRange = (newSlide) => {
    let newStart = Math.max(
      newSlide - Math.floor(maxVisiblePaginationDots / 2),
      0
    );
    let newEnd = Math.min(
      newStart + maxVisiblePaginationDots - 1,
      totalItems - 1
    );

    if (newEnd - newStart < maxVisiblePaginationDots - 1) {
      newStart = Math.max(newEnd - maxVisiblePaginationDots + 1, 0);
    }

    setVisiblePaginationRange([newStart, newEnd]);
  };

  const handlePaginationClick = (index) => {
    scrollToIndex(index);
    updatePaginationRange(index);
  };

  useEffect(() => {
    if (isMobile) {
      const scrollContainer = scrollContainerRef.current;

      const handleScroll = () => {
        let scrollLeft = scrollContainer.scrollLeft;
        const containerWidth = scrollContainer.clientWidth;
        const itemWidth = containerWidth * 0.83; // width: 83% in mobile always

        let newIndex;

        if (Dir === 'rtl') {
          const maxScrollLeft = scrollContainer.scrollWidth - containerWidth;
          if (scrollLeft < 0) {
            scrollLeft = Math.abs(scrollLeft);
          } else if (scrollLeft == -maxScrollLeft) {
            scrollLeft = maxScrollLeft;
          } else {
            scrollLeft = 0;
          }
        }

        newIndex = Math.round(scrollLeft / itemWidth);

        setActiveIndex(newIndex);
        updatePaginationRange(newIndex);
      };

      scrollContainer.addEventListener('scroll', handleScroll);

      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  const scrollToIndex = (index) => {
    const scrollContainer = scrollContainerRef.current;
    const containerWidth = scrollContainer.clientWidth;
    const itemWidth = containerWidth * (isMobile ? 0.83 : 0.33); // width: 83%
    const scrollLeft = index * itemWidth;

    // Temporarily disable 'scroll-snap-type', because it interferes with the 'scrollTo' in Chrome
    scrollContainer.style.scrollSnapType = 'none';

    if (Dir === 'ltr') {
      scrollContainer.scrollTo({
        left: isMobile ? scrollLeft - 15 : scrollLeft,
        behavior: 'smooth',
      });
    } else {
      scrollContainer.scrollTo({
        left: isMobile ? -scrollLeft + 15 : -scrollLeft,
        behavior: 'smooth',
      });
    }

    setTimeout(() => {
      // Re-enable 'scroll-snap-type' after the scrolling is done
      scrollContainer.style.scrollSnapType = 'x mandatory';
    }, 300);

    if (!isMobile) setActiveIndex(index);
    updatePaginationRange(index);
  };

  const nextSlide = () => {
    let newIndex;

    if (isArticlePage && !isMobile && activeIndex + 1 >= totalItems) {
      newIndex = 0;
    } else {
      newIndex =
        activeIndex < (isThreeSlidesLayout ? totalItems - 3 : totalItems - 1)
          ? activeIndex + 1
          : activeIndex;
    }

    scrollToIndex(newIndex);
  };

  const prevSlide = () => {
    let newIndex;

    if (isArticlePage && !isMobile && activeIndex === 0) {
      newIndex = totalItems - 1;
    } else {
      newIndex = activeIndex !== 0 ? activeIndex - 1 : 0;
    }

    scrollToIndex(newIndex);
  };

  useMatomoTrackEventOnChange(
    'Bildspel',
    'Bläddrar',
    href || 'novalue',
    activeIndex + 1
  );

  const dataPrintSidebarTitle = Heading || 'Mediakarusell';

  if (!Translations)
    throw new Error(`There is no Translation object for MediaGallery`);
  return (
    <div
      className={`c-media-gallery ${isFullWidthLayout ? 'full-width' : ''} ${
        isArticlePage ? 'article-page' : ''
      }`}
      data-print-sidebar-title={dataPrintSidebarTitle}
      data-type-media-slider={'MediaSlider'}
      data-is-member-of-chapter-container={
        props.isMemberOfChapterContainer || 'false'
      }
    >
      {Heading && (
        <SemanticHeader
          isH3={props.isH3}
          className={classNames({
            'c-media-gallery__heading': true,
            'c-media-gallery__heading__article-page': isArticlePage,
            'c-media-gallery__heading__h2-style':
              props.isMemberOfChapterContainer,
          })}
        >
          {Heading}
        </SemanticHeader>
      )}
      <div className="c-media-gallery__wrapper">
        {!!ShowPagination && (galleryIsScrollable || isMobile) && (
          <MediaGalleryPagination
            totalSlides={totalItems}
            currentSlide={activeIndex}
            visibleRange={visiblePaginationRange}
            handleClick={handlePaginationClick}
            hasGroupedDots={isThreeSlidesLayout}
          />
        )}

        <div className="c-media-gallery__content">
          <div className="c-media-gallery__items-container">
            <div
              className="c-media-gallery__items"
              aria-live="polite"
              ref={scrollContainerRef}
            >
              {hasMedia &&
                Media.map((media, index) =>
                  media.BlockTypeName === 'SlideshowImageBlock' ? (
                    <MediaGalleryImage
                      {...media}
                      key={index}
                      activeIndex={activeIndex}
                      index={index}
                      numberOfItems={totalItems}
                      pageProps={CurrentPageProps}
                      counter={props.Translations.counter}
                      isStanding={media.IsStandingImage}
                      slideWidth={slideWidth}
                      showAuthor={props.ShowAuthor}
                      translations={Translations}
                      isArticlePage={isArticlePage}
                      isMobile={isMobile}
                      alwaysShowCurrentImage={alwaysShowCurrentImageIndicator}
                    />
                  ) : (
                    <MediaGalleryVideo
                      {...media}
                      key={index}
                      activeIndex={activeIndex}
                      index={index}
                      numberOfItems={totalItems}
                      counter={props.Translations.counter}
                      slideWidth={slideWidth}
                      isMobile={isMobile}
                      alwaysShowCurrentImage={alwaysShowCurrentImageIndicator}
                    />
                  )
                )}
            </div>
          </div>
          {(galleryIsScrollable || isMobile) && (
            <div className="c-media-gallery__ctrls">
              <button
                type="button"
                className="c-media-gallery__nextprev c-media-gallery__nextprev--next"
                onClick={() => (Dir === 'ltr' ? nextSlide() : prevSlide())}
              >
                <span className="show-for-sr">
                  {Dir === 'ltr' ? Translations.next : Translations.previous}
                </span>
              </button>
              <button
                type="button"
                className="c-media-gallery__nextprev c-media-gallery__nextprev--prev"
                onClick={() => (Dir === 'ltr' ? prevSlide() : nextSlide())}
              >
                <span className="show-for-sr">
                  {Dir === 'ltr' ? Translations.previous : Translations.next}
                </span>
              </button>
            </div>
          )}
        </div>

        {props.Link && (props.LinkText || props.InternalLinkName) && (
          <div
            className={classNames({
              'c-media-gallery__link': true,
              'c-media-gallery__link__article-page': isArticlePage,
            })}
          >
            <a className="c-anchor c-linklist__link" href={props.Link}>
              {props.LinkText || props.InternalLinkName}
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaGallery;
